import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef, useId } from 'react';
import { useUserIdentityContext } from './UserIdentityContext';
import { useSelector } from 'react-redux';
import { RootState } from '@center-pwa/store/store';
import { useApiClientsOrNull } from './ApiClientsContext';
import { useMetaspaceEvents } from '../hooks/useMetaspaceEvents';
import { useSphereChangeEffect, type SphereChange } from '../hooks/useSphereChangeEffect';
import { notificationPollingService } from '../services/NotificationPollingService';
import { NotificationRecord } from '../components/organisms/NotificationFeed/useNotificationFeed';
import { Task } from '../components/molecules/TaskList/TaskListMolecule.types';
import { generateVirtualTasksFromSchedules, FactorScheduleInfo } from '../components/molecules/TaskList/scheduleResolver';
import { readTaskPages, type TaskPageResponse } from '../components/molecules/TaskList/readTaskPages';
import { isTransientReadFailure } from '../utils/isTransientReadFailure';
import type { TimelineEvent, FeedFilterState } from 'types/timeline';
import { DEFAULT_FILTER_STATE } from 'types/timeline';
import type { StreamChord } from 'types/stream';
import type { TrajectoryTrack, TrajectoryDemand, TrajectoryFreshnessSummary, TrajectoryRange } from 'types/trajectory';
import { loadFilterState, saveFilterState, getEnabledEventTypes, isDefaultFilterState } from '../config/feedFilterConfig';

interface NotificationState {
  notifications: NotificationRecord[];
  isLoading: boolean;
  error: string | null;
  unreadCount: number;
  hasMore: boolean;
}

interface TaskCounts {
  total: number;
  overdue: number;
  pending: number;
  workflow: number;
  checkIn: number;
}

interface TaskState {
  tasks: Task[];
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
}

interface TimelineState {
  events: TimelineEvent[];
  version: number | null;
  lastFetchedAt: string | null;
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  nextCursor: string | null;
}

export interface ConversationMessage {
  message_id: string;
  thread_id: string;
  author_id: string;
  content: string;
  created_at: string;
  author_name?: string;
  sphere_name?: string;
}

interface ConversationState {
  messages: ConversationMessage[];
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
}

/** Server-side stream filters — lens mappings (provenance tiers / actor kind) and aspect scoping.
 * Sent as `GET /spheres/:id/stream` query params; changing it refetches page 1. */
export interface StreamQuery {
  provenance?: string[];
  actorKind?: 'human' | 'system' | 'import';
  aspects?: string[];
}

export interface StreamState {
  chords: StreamChord[];
  hasMore: boolean;
  nextCursor: string | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Shared Trajectory state — `GET /spheres/:id/trajectory`'s tracks/freshness/demands bundle
 * (Trajectory tool, specs/stream-trajectory-tools.md Phase 5). Unlike Stream, the response is a
 * fixed bundle for the requested `range`, not a paginated feed — no cursor/hasMore.
 */
export interface TrajectoryState {
  tracks: TrajectoryTrack[];
  freshness: TrajectoryFreshnessSummary | null;
  demands: TrajectoryDemand[];
  range: TrajectoryRange;
  isLoading: boolean;
  error: string | null;
}

export interface ToolbarContextValue {
  /** The tracked sphere ID (driven by SPHERE_CENTERED events) */
  trackedSphereId: string | null;
  /** The tracked sphere type */
  trackedSphereType: string | null;
  /** Whether the user is viewing their own sphere */
  isViewingSelf: boolean;
  /** The current user's graph sphere ID (for assignee comparison in task cards) */
  currentUserId: string | null;
  /** The current user's identity ID (for notification API calls) */
  currentIdentityId: string | null;
  /** The current user's display name */
  currentUserName: string | null;

  /** Shared notification state */
  notificationState: NotificationState;
  /** Unread notifications only */
  unreadNotifications: NotificationRecord[];
  /** Mark a notification as read */
  markNotificationAsRead: (notificationId: string) => Promise<void>;
  /** Mark all notifications as read */
  markAllNotificationsAsRead: (filters?: { type?: string; sphereId?: string }) => Promise<void>;
  /** Refresh notifications */
  refreshNotifications: () => Promise<void>;
  /** Fetch more notifications (pagination) */
  fetchMoreNotifications: () => Promise<void>;

  /** Lightweight task counts (eagerly fetched for badges) */
  taskCounts: TaskCounts;

  /** Shared task state (lazily fetched when tasks panel is active) */
  taskState: TaskState;
  /** Refresh tasks */
  refreshTasks: () => Promise<void>;
  /** Fetch more tasks (pagination) */
  fetchMoreTasks: () => Promise<void>;

  /** Shared timeline state (activity feed events) */
  timelineState: TimelineState;
  /** Refresh timeline (full fetch, ignoring cursor) */
  refreshTimeline: () => Promise<void>;
  /** Fetch more timeline events (pagination) */
  fetchMoreTimeline: () => Promise<void>;

  /** Shared conversation message state (direct from conversation DB) */
  conversationState: ConversationState;
  /** Refresh conversation messages */
  refreshConversations: () => Promise<void>;
  /** Fetch more conversation messages (pagination) */
  fetchMoreConversations: () => Promise<void>;

  /**
   * Shared Stream state — `GET /spheres/:id/stream`'s chord-folded ledger (Stream tool,
   * specs/stream-trajectory-tools.md Phase 3). One store replaces the HomeMode/CompactTimeline
   * pair's independent `/timeline` fetches for this surface.
   */
  streamState: StreamState;
  /** Full refetch, ignoring the cursor. */
  refreshStream: () => Promise<void>;
  /** Fetch the next page of chords (pagination). */
  fetchMoreStream: () => Promise<void>;
  /** Active server-side stream filters (see `StreamQuery`). */
  streamQuery: StreamQuery;
  /** Replace the stream filters; page 1 refetches automatically when they change. */
  setStreamQuery: (query: StreamQuery) => void;

  /**
   * Shared Trajectory state — `GET /spheres/:id/trajectory`'s tracks/freshness/demands bundle
   * (Trajectory tool, specs/stream-trajectory-tools.md Phase 5).
   */
  trajectoryState: TrajectoryState;
  /** Refetch for a range (defaults to the current range). */
  refreshTrajectory: (range?: TrajectoryRange) => Promise<void>;

  /** Feed filter state (persisted per-sphere in localStorage) */
  feedFilters: FeedFilterState;
  /** Update feed filter state */
  setFeedFilters: (filters: FeedFilterState) => void;

  /** Count of overdue check-in periods for the current user (for toolbar badge) */
  overdueCheckInCount: number;

  /** Tell the context which toolbar panel is active (enables lazy data loading) */
  setActivePanel: (panel: string | null) => void;
}

const ToolbarContext = createContext<ToolbarContextValue | null>(null);

export interface ToolbarProviderProps {
  children: React.ReactNode;
  /** Initial sphere ID to start fetching for immediately */
  initialSphereId?: string;
  /** Notification polling interval in ms (default: 30000) */
  notificationPollInterval?: number;
  /** Items per page for notifications (default: 50) */
  notificationPageSize?: number;
  /** Task settings */
  taskSettings?: {
    includeVirtual?: boolean;
    lookaheadDays?: number;
  };
  /**
   * The last sphere the host's editor changed (`useEditSphereLauncher`). A task panel that has
   * loaded re-reads in the background, so its cards show the sphere as it is now.
   */
  sphereChange?: SphereChange | null;
}

const DEFAULT_NOTIFICATION_STATE: NotificationState = {
  notifications: [],
  isLoading: false,
  error: null,
  unreadCount: 0,
  hasMore: true,
};

const DEFAULT_TASK_STATE: TaskState = {
  tasks: [],
  isLoading: false,
  error: null,
  hasMore: true,
};

const DEFAULT_TIMELINE_STATE: TimelineState = {
  events: [],
  version: null,
  lastFetchedAt: null,
  isLoading: false,
  error: null,
  hasMore: true,
  nextCursor: null,
};

const DEFAULT_CONVERSATION_STATE: ConversationState = {
  messages: [],
  isLoading: false,
  error: null,
  hasMore: true,
};

const DEFAULT_STREAM_STATE: StreamState = {
  chords: [],
  hasMore: true,
  nextCursor: null,
  isLoading: false,
  error: null,
};

const DEFAULT_TRAJECTORY_STATE: TrajectoryState = {
  tracks: [],
  freshness: null,
  demands: [],
  range: '30d',
  isLoading: false,
  error: null,
};

const TIMELINE_PAGE_SIZE = 20;
const STREAM_PAGE_SIZE = 20;

// localStorage key helpers for timeline persistence (only stores sphereId + timestamp, no PII)
const FEED_CURSOR_KEY = (sphereId: string) => `feed:${sphereId}:cursor`;
const FEED_VERSION_KEY = (sphereId: string) => `feed:${sphereId}:version`;

function loadFeedState(sphereId: string): { cursor: string | null; version: number | null } {
  try {
    const cursor = localStorage.getItem(FEED_CURSOR_KEY(sphereId));
    const versionStr = localStorage.getItem(FEED_VERSION_KEY(sphereId));
    const version = versionStr === null ? null : Number(versionStr);
    return {
      cursor: cursor || null,
      version: version !== null && !Number.isNaN(version) ? version : null,
    };
  } catch {
    return { cursor: null, version: null };
  }
}

function saveFeedState(sphereId: string, cursor: string | null, version: number | null): void {
  try {
    if (cursor !== null) {
      localStorage.setItem(FEED_CURSOR_KEY(sphereId), cursor);
    }
    if (version !== null) {
      localStorage.setItem(FEED_VERSION_KEY(sphereId), version.toString());
    }
  } catch {
    // localStorage full or unavailable
  }
}

/**
 * ToolbarProvider - Provides shared notification/task/timeline state for the toolbar.
 *
 * Subscribes to SPHERE_CENTERED events to track which sphere the toolbar
 * should display data for. Accepts initialSphereId to start fetching immediately.
 */
export function ToolbarProvider({
  children,
  initialSphereId,
  notificationPollInterval = 30000,
  notificationPageSize = 50,
  taskSettings = { includeVirtual: false, lookaheadDays: 30 },
  sphereChange,
}: Readonly<ToolbarProviderProps>): React.ReactElement {
  const { identityId, displayName } = useUserIdentityContext();
  const apiClients = useApiClientsOrNull();
  const currentUserIdentityId = identityId;
  const currentUserDisplayName = displayName;
  // Graph sphere ID from Redux — used for task assignee comparison (matches task.assignee.id)
  const graphUserId = useSelector((state: RootState) => state.userProfile?.userId) ?? null;

  // Tracked sphere state (driven by SPHERE_CENTERED events)
  const [trackedSphereId, setTrackedSphereId] = useState<string | null>(initialSphereId ?? null);
  const [trackedSphereType, setTrackedSphereType] = useState<string | null>(null);

  // Active toolbar panel — enables lazy data loading (only fetch when panel is visible)
  const [activePanel, setActivePanel] = useState<string | null>(null);

  // Subscribe to SPHERE_CENTERED events
  const eventTypes = useMemo(() => ['SPHERE_CENTERED'], []);
  useMetaspaceEvents({
    subscriberId: 'toolbar-context',
    eventTypes,
    onEvent: (event) => {
      if (event.type === 'SPHERE_CENTERED' && event.payload?.sphereId) {
        const newSphereId = event.payload.sphereId;
        const newSphereType = event.payload.sphereType || null;

        setTrackedSphereId(prev => {
          if (prev === newSphereId) return prev;

          // Reset fetch tracking so effects re-trigger for the new sphere
          taskCountsFetchDoneRef.current = null;
          taskInitialFetchDoneRef.current = null;
          notificationListFetchDoneRef.current = null;
          conversationInitialFetchDoneRef.current = null;
          streamInitialFetchDoneRef.current = null;
          trajectoryInitialFetchDoneRef.current = null;
          overdueCheckInFetchDoneRef.current = null;
          setTimelineState(DEFAULT_TIMELINE_STATE);
          setConversationState(DEFAULT_CONVERSATION_STATE);
          setStreamState(DEFAULT_STREAM_STATE);
          setTrajectoryState(DEFAULT_TRAJECTORY_STATE);
          setOverdueCheckInCount(0);

          // Restore saved timeline state from localStorage for the new sphere
          const saved = loadFeedState(newSphereId);
          timelineVersionRef.current = saved.version;
          timelineCursorRef.current = saved.cursor;

          // Restore feed filter state for the new sphere
          setFeedFiltersRaw(loadFilterState(newSphereId));

          return newSphereId;
        });
        setTrackedSphereType(newSphereType);
      }
    },
  });

  // Determine if user is viewing their own sphere
  const isViewingSelf = useMemo(() => {
    if (!trackedSphereId || !currentUserIdentityId) return false;
    return trackedSphereType === 'subject' && trackedSphereId === currentUserIdentityId;
  }, [trackedSphereId, trackedSphereType, currentUserIdentityId]);

  // --- Notification state ---
  const [notificationState, setNotificationState] = useState<NotificationState>(DEFAULT_NOTIFICATION_STATE);
  const notificationOffsetRef = useRef(0);
  const notificationFetchInFlightRef = useRef(false);
  const notificationListFetchDoneRef = useRef<string | null>(null);
  // Stable per-provider key for the shared notification poller subscription.
  const notificationSubscriberId = `ToolbarContext:${useId()}`;

  // --- Task state ---
  const DEFAULT_TASK_COUNTS: TaskCounts = { total: 0, overdue: 0, pending: 0, workflow: 0, checkIn: 0 };
  const [taskCounts, setTaskCounts] = useState<TaskCounts>(DEFAULT_TASK_COUNTS);
  const taskCountsFetchInFlightRef = useRef(false);
  const taskCountsFetchDoneRef = useRef<string | null>(null);

  const [taskState, setTaskState] = useState<TaskState>(DEFAULT_TASK_STATE);
  const [factorSchedules, setFactorSchedules] = useState<FactorScheduleInfo[]>([]);
  const [materializedTasks, setMaterializedTasks] = useState<Task[]>([]);
  const taskOffsetRef = useRef(0);
  const taskFetchInFlightRef = useRef(false);
  const taskInitialFetchDoneRef = useRef<string | null>(null);
  const taskQuietRefreshPendingRef = useRef(false);
  const fetchTasksRef = useRef<(reset?: boolean, quiet?: boolean) => Promise<void>>(() => Promise.resolve());

  // --- Timeline state (activity feed) ---
  const [timelineState, setTimelineState] = useState<TimelineState>(DEFAULT_TIMELINE_STATE);
  const timelineVersionRef = useRef<number | null>(null);
  const timelineCursorRef = useRef<string | null>(null);
  const timelineInitialFetchInFlightRef = useRef(false);
  const timelinePaginationFetchInFlightRef = useRef(false);

  // --- Conversation state (direct messages from conversation DB) ---
  const [conversationState, setConversationState] = useState<ConversationState>(DEFAULT_CONVERSATION_STATE);
  const conversationOffsetRef = useRef(0);
  const conversationFetchInFlightRef = useRef(false);
  const conversationInitialFetchDoneRef = useRef<string | null>(null);

  // --- Stream state (chord-folded ledger) ---
  const [streamState, setStreamState] = useState<StreamState>(DEFAULT_STREAM_STATE);
  const [streamQuery, setStreamQuery] = useState<StreamQuery>({});
  const streamFetchInFlightRef = useRef(false);
  const streamInitialFetchDoneRef = useRef<string | null>(null);

  // --- Trajectory state (per-aspect tracks, cadence, demands) ---
  const [trajectoryState, setTrajectoryState] = useState<TrajectoryState>(DEFAULT_TRAJECTORY_STATE);
  const trajectoryFetchInFlightRef = useRef(false);
  const trajectoryInitialFetchDoneRef = useRef<string | null>(null);

  // --- Feed filter state (persisted per-sphere in localStorage) ---
  const [feedFilters, setFeedFiltersRaw] = useState<FeedFilterState>(
    () => (initialSphereId ? loadFilterState(initialSphereId) : { ...DEFAULT_FILTER_STATE })
  );

  const setFeedFilters = useCallback((filters: FeedFilterState) => {
    setFeedFiltersRaw(filters);
    if (trackedSphereId) {
      saveFilterState(trackedSphereId, filters);
    }
  }, [trackedSphereId]);

  // Ref for feed filters so fetchTimeline/pollTimeline can read current value
  // without adding feedFilters to their dependency arrays
  const feedFiltersRef = useRef<FeedFilterState>(feedFilters);
  feedFiltersRef.current = feedFilters;

  // Restore timeline state from localStorage on initial mount
  const timelineRestoredRef = useRef(false);
  if (!timelineRestoredRef.current && initialSphereId) {
    timelineRestoredRef.current = true;
    const saved = loadFeedState(initialSphereId);
    timelineVersionRef.current = saved.version;
    timelineCursorRef.current = saved.cursor;
  }

  // --- Notification fetching ---

  // The unread badge count is owned by `notificationPollingService` (see the
  // subscription effect below), not fetched here. This context used to poll
  // `/stats` on its own timer while `useNotificationFeed` and the polling service
  // did the same, so one page load issued three identical requests — two of them
  // in the same millisecond — and then ran three polling loops against one endpoint.
  // This remains callable so mark-as-read paths can force an out-of-band refresh.
  const fetchNotificationStats = useCallback(async () => {
    await notificationPollingService.refresh();
  }, []);

  const fetchNotifications = useCallback(async (reset = true) => {
    if (!currentUserIdentityId || !apiClients?.notificationClient) return;
    if (notificationFetchInFlightRef.current) return;

    notificationFetchInFlightRef.current = true;

    if (reset) {
      setNotificationState(prev => ({ ...prev, isLoading: true, error: null }));
      notificationOffsetRef.current = 0;
    }

    try {
      const currentOffset = reset ? 0 : notificationOffsetRef.current;

      const params: Record<string, string | number> = {
        limit: notificationPageSize,
        offset: currentOffset,
      };

      // Only filter by sphereId if we have a tracked sphere, know its type, and it's not self
      const shouldFilterBySphere = trackedSphereId && trackedSphereType && !isViewingSelf;
      if (shouldFilterBySphere) {
        params.sphereId = trackedSphereId;
      }

      const response = await apiClients.notificationClient.get(`/api/notifications/${currentUserIdentityId}`, {
        params,
      });

      let fetchedNotifications: NotificationRecord[] = [];
      if (response.data?.success) {
        fetchedNotifications = response.data.data;
      } else if (Array.isArray(response.data)) {
        fetchedNotifications = response.data;
      }

      setNotificationState(prev => {
        const allNotifications = reset ? fetchedNotifications : [...prev.notifications, ...fetchedNotifications];
        const unreadCount = allNotifications.filter(n => n.status !== 'read').length;

        return {
          ...prev,
          notifications: allNotifications,
          unreadCount: unreadCount,
          hasMore: fetchedNotifications.length === notificationPageSize,
          isLoading: false,
        };
      });

      notificationOffsetRef.current = currentOffset + fetchedNotifications.length;
    } catch (error: unknown) {
      console.error('[ToolbarContext] Failed to fetch notifications:', error);
      setNotificationState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'notifications.error.loadFailed',
        isLoading: false,
      }));
    } finally {
      notificationFetchInFlightRef.current = false;
    }
  }, [currentUserIdentityId, apiClients?.notificationClient, notificationPageSize, isViewingSelf, trackedSphereId, trackedSphereType]);

  const markNotificationAsRead = useCallback(async (notificationId: string) => {
    if (!currentUserIdentityId || !apiClients?.notificationClient) return;

    try {
      await apiClients.notificationClient.patch(`/api/notifications/${notificationId}/read`, {
        userId: currentUserIdentityId,
      });

      setNotificationState(prev => ({
        ...prev,
        notifications: prev.notifications.map(n =>
          n.id === notificationId ? { ...n, status: 'read' as const, read_at: new Date().toISOString() } : n
        ),
        unreadCount: Math.max(0, prev.unreadCount - 1),
      }));
    } catch (error) {
      console.error('[ToolbarContext] Failed to mark notification as read:', error);
    }
  }, [currentUserIdentityId, apiClients?.notificationClient]);

  const markAllNotificationsAsRead = useCallback(async (filters?: { type?: string; sphereId?: string }) => {
    if (!currentUserIdentityId || !apiClients?.notificationClient) return;

    try {
      await apiClients.notificationClient.patch(`/api/notifications/${currentUserIdentityId}/mark-all-read`, filters || {});

      setNotificationState(prev => {
        const updatedNotifications = prev.notifications.map(n => {
          if (filters?.type && n.type !== filters.type) return n;
          if (filters?.sphereId && n.sphere_id !== filters.sphereId) return n;
          if (n.status !== 'read') {
            return { ...n, status: 'read' as const, read_at: new Date().toISOString() };
          }
          return n;
        });

        const unreadCount = updatedNotifications.filter(n => n.status !== 'read').length;

        return {
          ...prev,
          notifications: updatedNotifications,
          unreadCount: unreadCount,
        };
      });

      await fetchNotificationStats().catch(() => {
        // Ignore stats fetch error - local count already updated
      });
    } catch (error) {
      console.error('[ToolbarContext] Failed to mark all notifications as read:', error);
    }
  }, [currentUserIdentityId, apiClients?.notificationClient, fetchNotificationStats]);

  const refreshNotifications = useCallback(async () => {
    await Promise.all([fetchNotifications(true), fetchNotificationStats()]);
  }, [fetchNotifications, fetchNotificationStats]);

  const fetchMoreNotifications = useCallback(async () => {
    if (!notificationState.isLoading && notificationState.hasMore) {
      await fetchNotifications(false);
    }
  }, [fetchNotifications, notificationState.isLoading, notificationState.hasMore]);

  // --- Task counts fetching (lightweight, for badges) ---

  const fetchTaskCounts = useCallback(async () => {
    if (!trackedSphereId || !apiClients?.graphClient) return;
    if (taskCountsFetchInFlightRef.current) return;

    taskCountsFetchInFlightRef.current = true;

    try {
      const response = await apiClients.graphClient.get(
        `/tasks/${trackedSphereId}/counts`
      );

      if (response.success && response.counts) {
        setTaskCounts(response.counts as TaskCounts);
      }
    } catch (error) {
      console.error('[ToolbarContext] Failed to fetch task counts:', error);
    } finally {
      taskCountsFetchInFlightRef.current = false;
    }
  }, [trackedSphereId, apiClients?.graphClient]);

  // --- Task fetching (full list, lazy) ---

  // `quiet` re-reads in the background: no loading state, and a transient failure (network, cancel, 429,
  // 5xx) keeps the cards on screen; a refusal (403, 404, anything else) takes the normal failure path (D71).
  // It re-reads every page already loaded, and the offset moves only when a read succeeds.
  // A quiet re-read asked for while a fetch is in flight runs when that fetch ends, because the
  // fetch in flight may have been sent before the change it is meant to show.
  const fetchTasks = useCallback(async (reset = true, quiet = false) => {
    if (!trackedSphereId || !apiClients?.graphClient) return;
    if (taskFetchInFlightRef.current) {
      if (quiet) taskQuietRefreshPendingRef.current = true;
      return;
    }

    taskFetchInFlightRef.current = true;

    if (reset && !quiet) setTaskState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const pageSize = 50;
      const graphClient = apiClients.graphClient;
      const readPage = (pageOffset: number): Promise<TaskPageResponse & { factorSchedules?: FactorScheduleInfo[] }> => {
        const params = new URLSearchParams();
        params.set('limit', pageSize.toString());
        params.set('offset', pageOffset.toString());
        if (taskSettings.includeVirtual !== undefined) {
          params.set('includeVirtual', taskSettings.includeVirtual.toString());
        }
        params.set('sortBy', 'dueAt');
        params.set('sortOrder', 'asc');
        return graphClient.get(`/tasks/${trackedSphereId}?${params.toString()}`);
      };
      const loaded = taskOffsetRef.current;
      const { pages, tasks: fetchedTasks, nextOffset, hasMore } = await readTaskPages(
        readPage,
        reset ? 0 : loaded,
        reset && quiet ? loaded : 0
      );

      if (reset) {
        setMaterializedTasks(fetchedTasks);
        setFactorSchedules(pages[0].factorSchedules || []);
      } else {
        setMaterializedTasks(prev => [...prev, ...fetchedTasks]);
      }

      taskOffsetRef.current = nextOffset;

      setTaskState(prev => ({
        ...prev,
        hasMore,
        isLoading: false,
        ...(quiet && { error: null }),
      }));
    } catch (error: unknown) {
      console.error('[ToolbarContext] Failed to fetch tasks:', error);
      if (quiet && isTransientReadFailure(error)) return;
      setTaskState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'tasks.error.loadFailed',
        isLoading: false,
      }));
      if (reset) {
        setMaterializedTasks([]);
        setFactorSchedules([]);
        taskOffsetRef.current = 0;
      }
    } finally {
      taskFetchInFlightRef.current = false;
      if (taskQuietRefreshPendingRef.current) {
        taskQuietRefreshPendingRef.current = false;
        void fetchTasksRef.current(true, true);
      }
    }
  }, [trackedSphereId, apiClients?.graphClient, taskSettings.includeVirtual]);
  fetchTasksRef.current = fetchTasks;

  // A sphere changed in the host's editor: a task panel that has loaded re-reads quietly. One that
  // has not loaded reads the change when it first opens.
  useSphereChangeEffect(sphereChange, () => {
    if (trackedSphereId && taskInitialFetchDoneRef.current === trackedSphereId) {
      void fetchTasks(true, true);
    }
  });

  const refreshTasks = useCallback(async () => {
    await fetchTasks(true);
  }, [fetchTasks]);

  const fetchMoreTasks = useCallback(async () => {
    if (!taskState.isLoading && taskState.hasMore) {
      await fetchTasks(false);
    }
  }, [fetchTasks, taskState.isLoading, taskState.hasMore]);

  // --- Timeline fetching ---

  const fetchTimeline = useCallback(async (options: { reset: boolean; before?: string }) => {
    if (!trackedSphereId || !apiClients?.graphClient) return;

    const { reset } = options;
    const inFlightRef = reset ? timelineInitialFetchInFlightRef : timelinePaginationFetchInFlightRef;
    if (inFlightRef.current) return;

    inFlightRef.current = true;

    if (reset) {
      setTimelineState(prev => ({ ...prev, isLoading: true, error: null }));
    } else {
      setTimelineState(prev => ({ ...prev, isLoading: true }));
    }

    try {
      const params = new URLSearchParams();
      params.set('limit', TIMELINE_PAGE_SIZE.toString());
      params.set('sortOrder', 'desc');
      if (!reset && options.before) {
        params.set('before', options.before);
      }

      // Include eventTypes param when filters reduce the set
      if (!isDefaultFilterState(feedFiltersRef.current)) {
        const enabledTypes = getEnabledEventTypes(feedFiltersRef.current);
        if (enabledTypes.length > 0) {
          params.set('eventTypes', enabledTypes.join(','));
        }
      }

      const response = await apiClients.graphClient.get(
        `/spheres/${trackedSphereId}/timeline?${params.toString()}`
      );

      if (response.success) {
        // Validate response shape — API may return events under `data` or `events`
        let fetchedEvents: TimelineEvent[];
        if (Array.isArray(response.data)) {
          fetchedEvents = response.data as TimelineEvent[];
        } else if (Array.isArray(response.events)) {
          fetchedEvents = response.events as TimelineEvent[];
        } else {
          console.warn('[ToolbarContext] Unexpected timeline response shape:', {
            hasData: response.data !== undefined,
            dataType: typeof response.data,
            hasEvents: response.events !== undefined,
          });
          fetchedEvents = [];
        }

        setTimelineState(prev => {
          let allEvents: TimelineEvent[];
          if (reset) {
            allEvents = fetchedEvents;
          } else {
            const existingIds = new Set(prev.events.map((e: TimelineEvent) => e.id));
            const newEvents = fetchedEvents.filter((e: TimelineEvent) => !existingIds.has(e.id));
            allEvents = [...prev.events, ...newEvents];
          }

          const latestTimestamp = allEvents.length > 0 ? allEvents[0].timestamp : null;
          if (latestTimestamp) {
            timelineCursorRef.current = latestTimestamp;
          }

          if (trackedSphereId) {
            saveFeedState(trackedSphereId, timelineCursorRef.current, timelineVersionRef.current);
          }

          return {
            ...prev,
            events: allEvents,
            lastFetchedAt: new Date().toISOString(),
            hasMore: response.hasMore ?? (response.total == null ? fetchedEvents.length === TIMELINE_PAGE_SIZE : allEvents.length < response.total),
            nextCursor: (response.nextCursor as string | null | undefined) ?? null,
            isLoading: false,
            error: null,
          };
        });
      } else {
        throw new Error('Timeline fetch returned unsuccessful response');
      }
    } catch (error: unknown) {
      console.error('[ToolbarContext] Failed to fetch timeline:', error);
      setTimelineState(prev => ({
        ...prev,
        error: 'activityFeed.error',
        isLoading: false,
      }));
    } finally {
      inFlightRef.current = false;
    }
  }, [trackedSphereId, apiClients?.graphClient]);

  const refreshTimeline = useCallback(async () => {
    timelineVersionRef.current = null;
    timelineCursorRef.current = null;
    await fetchTimeline({ reset: true });
  }, [fetchTimeline]);

  const fetchMoreTimeline = useCallback(async () => {
    // "More" only exists after the first page. Feed panels probe on mount
    // (ResizeObserver: "content does not fill the container") before that page
    // has been requested; without this guard that probe issued a second copy
    // of the initial query.
    if (timelineState.lastFetchedAt === null) return;
    if (!timelineState.isLoading && timelineState.hasMore) {
      await fetchTimeline({ reset: false, before: timelineState.nextCursor ?? undefined });
    }
  }, [fetchTimeline, timelineState.isLoading, timelineState.hasMore, timelineState.nextCursor, timelineState.lastFetchedAt]);

  // --- Overdue check-in count (for status toolbar badge) ---

  const [overdueCheckInCount, setOverdueCheckInCount] = useState(0);
  const overdueCheckInFetchInFlightRef = useRef(false);
  const overdueCheckInFetchDoneRef = useRef<string | null>(null);

  const fetchOverdueCheckInCount = useCallback(async () => {
    if (!trackedSphereId || !apiClients?.graphClient || !currentUserIdentityId) {
      setOverdueCheckInCount(0);
      return;
    }
    if (overdueCheckInFetchInFlightRef.current) return;
    overdueCheckInFetchInFlightRef.current = true;

    try {
      const statusResponse = await apiClients.graphClient.get(
        `/sphere/${trackedSphereId}/points/status`
      );

      const statusPoint = statusResponse?.point;
      const factors = statusPoint?.currentValue?.factors;
      if (!Array.isArray(factors)) {
        setOverdueCheckInCount(0);
        return;
      }

      const manualFactors = factors.filter(
        (f: { factorType: string; id: string }) => f.factorType === 'manual'
      );
      if (manualFactors.length === 0) {
        setOverdueCheckInCount(0);
        return;
      }

      const periodResults = await Promise.all(
        manualFactors.map(async (factor: { factorType: string; id: string }) => {
          try {
            const response = await apiClients.graphClient.get(
              `/factors/${factor.id}/periods?status=overdue&limit=5`
            );
            if (response.success && Array.isArray(response.periods)) {
              return response.periods as Array<{
                responsibleUsers?: Array<{
                  userId: string;
                  checkedIn?: boolean;
                  skipped?: boolean;
                }>;
              }>;
            }
            return [];
          } catch {
            return [];
          }
        })
      );

      let count = 0;
      for (const periods of periodResults) {
        for (const period of periods) {
          const isResponsible = period.responsibleUsers?.some(
            (u) =>
              u.userId === currentUserIdentityId &&
              !u.checkedIn &&
              !u.skipped
          );
          if (isResponsible) count++;
        }
      }

      setOverdueCheckInCount(count);
    } catch (error) {
      console.error('[ToolbarContext] Failed to fetch overdue check-in count:', error);
      setOverdueCheckInCount(0);
    } finally {
      overdueCheckInFetchInFlightRef.current = false;
    }
  }, [trackedSphereId, apiClients?.graphClient, currentUserIdentityId]);

  // --- Conversation fetching ---

  const CONVERSATION_PAGE_SIZE = 20;

  const fetchConversations = useCallback(async (reset = true) => {
    if (!trackedSphereId || !apiClients?.graphClient) return;
    if (conversationFetchInFlightRef.current) return;

    conversationFetchInFlightRef.current = true;

    if (reset) {
      setConversationState(prev => ({ ...prev, isLoading: true, error: null }));
      conversationOffsetRef.current = 0;
    } else {
      setConversationState(prev => ({ ...prev, isLoading: true }));
    }

    try {
      const currentOffset = reset ? 0 : conversationOffsetRef.current;
      const response = await apiClients.graphClient.get(
        `/conversation/sphere/${trackedSphereId}/recent-messages?limit=${CONVERSATION_PAGE_SIZE}&offset=${currentOffset}`
      );

      if (response.success) {
        const fetchedMessages: ConversationMessage[] = response.messages || [];

        setConversationState(prev => {
          const allMessages = reset
            ? fetchedMessages
            : [...prev.messages, ...fetchedMessages.filter(
                (m: ConversationMessage) => !prev.messages.some(existing => existing.message_id === m.message_id)
              )];

          return {
            ...prev,
            messages: allMessages,
            hasMore: response.hasMore ?? fetchedMessages.length === CONVERSATION_PAGE_SIZE,
            isLoading: false,
            error: null,
          };
        });

        conversationOffsetRef.current = currentOffset + fetchedMessages.length;
      } else {
        throw new Error('Failed to fetch conversation messages');
      }
    } catch (error: unknown) {
      console.error('[ToolbarContext] Failed to fetch conversations:', error);
      setConversationState(prev => ({
        ...prev,
        error: 'conversations.error.loadFailed',
        isLoading: false,
      }));
    } finally {
      conversationFetchInFlightRef.current = false;
    }
  }, [trackedSphereId, apiClients?.graphClient]);

  const refreshConversations = useCallback(async () => {
    await fetchConversations(true);
  }, [fetchConversations]);

  const fetchMoreConversations = useCallback(async () => {
    if (!conversationState.isLoading && conversationState.hasMore) {
      await fetchConversations(false);
    }
  }, [fetchConversations, conversationState.isLoading, conversationState.hasMore]);

  // --- Stream fetching (GET /spheres/:id/stream — chord-folded ledger) ---

  const fetchStream = useCallback(async (options: { reset: boolean; before?: string }) => {
    if (!trackedSphereId || !apiClients?.graphClient) return;
    if (streamFetchInFlightRef.current) return;

    streamFetchInFlightRef.current = true;
    setStreamState(prev => ({ ...prev, isLoading: true, error: options.reset ? null : prev.error }));

    try {
      const params = new URLSearchParams();
      params.set('limit', STREAM_PAGE_SIZE.toString());
      if (!options.reset && options.before) {
        params.set('before', options.before);
      }
      params.set('tz', Intl.DateTimeFormat().resolvedOptions().timeZone);
      if (streamQuery.provenance?.length) params.set('provenance', streamQuery.provenance.join(','));
      if (streamQuery.actorKind) params.set('actorKind', streamQuery.actorKind);
      if (streamQuery.aspects?.length) params.set('aspects', streamQuery.aspects.join(','));

      const response = await apiClients.graphClient.get(`/spheres/${encodeURIComponent(trackedSphereId)}/stream?${params.toString()}`);
      const fetchedChords: StreamChord[] = Array.isArray(response?.chords) ? response.chords : [];

      setStreamState(prev => ({
        ...prev,
        chords: options.reset ? fetchedChords : [...prev.chords, ...fetchedChords],
        hasMore: response?.hasMore ?? false,
        nextCursor: response?.nextCursor ?? null,
        isLoading: false,
        error: null,
      }));
    } catch (error: unknown) {
      console.error('[ToolbarContext] Failed to fetch stream:', error);
      setStreamState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'stream.error.loadFailed',
        isLoading: false,
      }));
    } finally {
      streamFetchInFlightRef.current = false;
    }
  }, [trackedSphereId, apiClients?.graphClient, streamQuery]);

  const refreshStream = useCallback(async () => {
    await fetchStream({ reset: true });
  }, [fetchStream]);

  const fetchMoreStream = useCallback(async () => {
    if (!streamState.isLoading && streamState.hasMore && streamState.nextCursor) {
      await fetchStream({ reset: false, before: streamState.nextCursor });
    }
  }, [fetchStream, streamState.isLoading, streamState.hasMore, streamState.nextCursor]);

  // --- Trajectory fetching (GET /spheres/:id/trajectory — tracks/freshness/demands bundle) ---

  const fetchTrajectory = useCallback(async (range?: TrajectoryRange) => {
    if (!trackedSphereId || !apiClients?.graphClient) return;
    if (trajectoryFetchInFlightRef.current) return;

    trajectoryFetchInFlightRef.current = true;
    const effectiveRange = range ?? trajectoryState.range;
    setTrajectoryState(prev => ({ ...prev, isLoading: true, error: null, range: effectiveRange }));

    try {
      const response = await apiClients.graphClient.get(`/spheres/${encodeURIComponent(trackedSphereId)}/trajectory?range=${effectiveRange}`);

      setTrajectoryState(prev => ({
        ...prev,
        tracks: Array.isArray(response?.tracks) ? response.tracks : [],
        freshness: response?.freshness ?? null,
        demands: Array.isArray(response?.demands) ? response.demands : [],
        range: response?.range ?? effectiveRange,
        isLoading: false,
        error: null,
      }));
    } catch (error: unknown) {
      console.error('[ToolbarContext] Failed to fetch trajectory:', error);
      setTrajectoryState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'trajectory.error.loadFailed',
        isLoading: false,
      }));
    } finally {
      trajectoryFetchInFlightRef.current = false;
    }
  }, [trackedSphereId, apiClients?.graphClient, trajectoryState.range]);

  const refreshTrajectory = useCallback(async (range?: TrajectoryRange) => {
    await fetchTrajectory(range);
  }, [fetchTrajectory]);

  // --- Computed tasks (with virtual task generation) ---

  const computedTasks = useMemo(() => {
    const includeVirtual = taskSettings.includeVirtual !== false;
    const lookaheadDays = taskSettings.lookaheadDays || 90;

    let allTasks: Task[];

    if (!includeVirtual || factorSchedules.length === 0) {
      allTasks = [...materializedTasks];
    } else {
      const existingTaskDueDates = new Map<string, Set<string>>();
      for (const task of materializedTasks) {
        if (task.taskType === 'check_in' && task.factorId && task.dueAt) {
          let dateSet = existingTaskDueDates.get(task.factorId);
          if (!dateSet) {
            dateSet = new Set();
            existingTaskDueDates.set(task.factorId, dateSet);
          }
          dateSet.add(task.dueAt);
        }
      }

      const virtualTasks = generateVirtualTasksFromSchedules(
        factorSchedules,
        existingTaskDueDates,
        lookaheadDays
      );

      allTasks = [...materializedTasks, ...virtualTasks as Task[]];
    }

    allTasks.sort((a, b) => {
      const aDate = a.dueAt ? new Date(a.dueAt).getTime() : Infinity;
      const bDate = b.dueAt ? new Date(b.dueAt).getTime() : Infinity;
      return aDate - bDate;
    });

    return allTasks;
  }, [materializedTasks, factorSchedules, taskSettings.includeVirtual, taskSettings.lookaheadDays]);

  // Update task state when computed tasks change
  useEffect(() => {
    setTaskState(prev => ({ ...prev, tasks: computedTasks }));
  }, [computedTasks]);

  // --- Effects: initial data fetch and polling ---

  // Subscribe to the shared notification poller for the badge count. The service
  // fetches once for all subscribers, serves late arrivals from its cache, and
  // polls at the fastest cadence any subscriber asks for — so mounting this
  // provider alongside a notification feed costs no additional requests.
  // The full notification list stays lazy (fetched when the panel opens).
  useEffect(() => {
    const notificationClient = apiClients?.notificationClient;
    if (!currentUserIdentityId || !notificationClient) return;

    notificationPollingService.subscribe(
      notificationSubscriberId,
      currentUserIdentityId,
      notificationClient,
      (_stats, unread) => {
        setNotificationState(prev => (prev.unreadCount === unread ? prev : { ...prev, unreadCount: unread }));
      },
      (error) => {
        console.error('[ToolbarContext] Notification polling error:', error);
      },
      { pollIntervalMs: notificationPollInterval }
    );

    return () => notificationPollingService.unsubscribe(notificationSubscriberId);
  }, [notificationSubscriberId, currentUserIdentityId, apiClients?.notificationClient, notificationPollInterval]);

  // Lazy: fetch full notifications list when notifications panel becomes active
  useEffect(() => {
    if (activePanel === 'notifications' && trackedSphereId && apiClients?.notificationClient) {
      if (notificationListFetchDoneRef.current !== trackedSphereId) {
        notificationListFetchDoneRef.current = trackedSphereId;
        fetchNotifications(true);
      }
    }
  }, [activePanel, trackedSphereId, apiClients?.notificationClient, fetchNotifications]);

  // Eagerly fetch task counts (for badge display)
  useEffect(() => {
    if (trackedSphereId && apiClients?.graphClient) {
      if (taskCountsFetchDoneRef.current !== trackedSphereId) {
        taskCountsFetchDoneRef.current = trackedSphereId;
        fetchTaskCounts();
      }
    }
  }, [trackedSphereId, apiClients?.graphClient, fetchTaskCounts]);

  // Lazy: fetch full task list when tasks panel becomes active
  useEffect(() => {
    if (activePanel === 'tasks' && trackedSphereId && apiClients?.graphClient) {
      if (taskInitialFetchDoneRef.current !== trackedSphereId) {
        taskInitialFetchDoneRef.current = trackedSphereId;
        refreshTasks();
      }
    }
  }, [activePanel, trackedSphereId, apiClients?.graphClient, refreshTasks]);

  // Refresh timeline when API-affecting filter state changes (same sphere)
  const prevFilterStateRef = useRef<{ sphereId: string | null; enabledTypesKey: string } | null>(null);

  useEffect(() => {
    const enabledTypes = getEnabledEventTypes(feedFilters);
    const key = enabledTypes.join(',');
    const prev = prevFilterStateRef.current;

    if (prev !== null && prev.sphereId === trackedSphereId && prev.enabledTypesKey !== key) {
      refreshTimeline();
    }
    prevFilterStateRef.current = { sphereId: trackedSphereId, enabledTypesKey: key };
  }, [feedFilters, trackedSphereId, refreshTimeline]);

  // Lazy: fetch conversations when chatbox/stream panel becomes active. `stream` (not the
  // legacy `activity`, which `resolveToolbarItemAlias` never produces anymore) needs
  // conversation messages too — the Stream panel merges them client-side as quiet chat clusters.
  useEffect(() => {
    const isConversationPanel = activePanel === 'chatbox' || activePanel === 'stream';
    if (isConversationPanel && trackedSphereId && apiClients?.graphClient) {
      if (conversationInitialFetchDoneRef.current !== trackedSphereId) {
        conversationInitialFetchDoneRef.current = trackedSphereId;
        refreshConversations();
      }
    }
  }, [activePanel, trackedSphereId, apiClients?.graphClient, refreshConversations]);

  // Lazy: fetch the Stream when the stream panel becomes active (or sphere changes while active)
  useEffect(() => {
    if (activePanel === 'stream' && trackedSphereId && apiClients?.graphClient) {
      if (streamInitialFetchDoneRef.current !== trackedSphereId) {
        streamInitialFetchDoneRef.current = trackedSphereId;
        refreshStream();
      }
    }
  }, [activePanel, trackedSphereId, apiClients?.graphClient, refreshStream]);

  // Refetch page 1 when the stream filters change (lens applied/cleared, aspect scope set) —
  // skipped until an initial fetch has happened so mounting with default filters stays lazy.
  const prevStreamQueryRef = useRef<StreamQuery | null>(null);
  useEffect(() => {
    const prev = prevStreamQueryRef.current;
    prevStreamQueryRef.current = streamQuery;
    if (prev === null || prev === streamQuery) return;
    if (streamInitialFetchDoneRef.current !== trackedSphereId) return;
    refreshStream();
  }, [streamQuery, trackedSphereId, refreshStream]);

  // Lazy: fetch the Trajectory bundle when the trajectory panel becomes active (or sphere changes)
  useEffect(() => {
    if (activePanel === 'trajectory' && trackedSphereId && apiClients?.graphClient) {
      if (trajectoryInitialFetchDoneRef.current !== trackedSphereId) {
        trajectoryInitialFetchDoneRef.current = trackedSphereId;
        refreshTrajectory();
      }
    }
  }, [activePanel, trackedSphereId, apiClients?.graphClient, refreshTrajectory]);

  // Fetch overdue check-in count when trackedSphereId changes
  useEffect(() => {
    if (trackedSphereId && apiClients?.graphClient) {
      if (overdueCheckInFetchDoneRef.current !== trackedSphereId) {
        overdueCheckInFetchDoneRef.current = trackedSphereId;
        fetchOverdueCheckInCount();
      }
    }
  }, [trackedSphereId, apiClients?.graphClient, fetchOverdueCheckInCount]);

  // Compute unread notifications
  const unreadNotifications = useMemo(() => {
    return notificationState.notifications.filter(n => n.status !== 'read');
  }, [notificationState.notifications]);

  const value = useMemo<ToolbarContextValue>(
    () => ({
      trackedSphereId,
      trackedSphereType,
      isViewingSelf,
      currentUserId: graphUserId,
      currentIdentityId: currentUserIdentityId || null,
      currentUserName: currentUserDisplayName,
      notificationState,
      unreadNotifications,
      markNotificationAsRead,
      markAllNotificationsAsRead,
      refreshNotifications,
      fetchMoreNotifications,
      taskCounts,
      taskState,
      refreshTasks,
      fetchMoreTasks,
      timelineState,
      refreshTimeline,
      fetchMoreTimeline,
      conversationState,
      refreshConversations,
      fetchMoreConversations,
      streamState,
      refreshStream,
      fetchMoreStream,
      streamQuery,
      setStreamQuery,
      trajectoryState,
      refreshTrajectory,
      feedFilters,
      setFeedFilters,
      overdueCheckInCount,
      setActivePanel,
    }),
    [
      trackedSphereId,
      trackedSphereType,
      isViewingSelf,
      graphUserId,
      currentUserIdentityId,
      notificationState,
      unreadNotifications,
      markNotificationAsRead,
      markAllNotificationsAsRead,
      refreshNotifications,
      fetchMoreNotifications,
      taskCounts,
      taskState,
      refreshTasks,
      fetchMoreTasks,
      timelineState,
      refreshTimeline,
      fetchMoreTimeline,
      conversationState,
      refreshConversations,
      fetchMoreConversations,
      streamState,
      refreshStream,
      fetchMoreStream,
      streamQuery,
      setStreamQuery,
      trajectoryState,
      refreshTrajectory,
      feedFilters,
      setFeedFilters,
      overdueCheckInCount,
      setActivePanel,
    ]
  );

  return <ToolbarContext.Provider value={value}>{children}</ToolbarContext.Provider>;
}

/**
 * useToolbarContext - Hook to access toolbar shared state
 *
 * @throws Error if used outside of ToolbarProvider
 */
export function useToolbarContext(): ToolbarContextValue {
  const context = useContext(ToolbarContext);
  if (!context) {
    throw new Error('useToolbarContext must be used within a ToolbarProvider');
  }
  return context;
}

/**
 * useToolbarContextOrNull - Hook to access toolbar shared state (optional)
 *
 * Returns null if used outside of ToolbarProvider
 */
export function useToolbarContextOrNull(): ToolbarContextValue | null {
  return useContext(ToolbarContext);
}

export default ToolbarContext;
